<template>
  <article>
    <div class="analytics-top">
      <div class="grid grid--container">
        <div class="analytics-top__content">
          <VText class="analytics-top__title" variant="h1"
            >Advanced analytics and insights for your link-in-bio</VText
          >
          <VText class="analytics-top__text">
            We turn your analytics into actionable insights to help you make
            data driven decisions. Faster insights help convert your followers
            into customers.</VText
          >
          <div>
            <Button
              href="https://app.campsite.bio/create-account"
              size="large"
              color="secondary"
              variant="hard-shadow"
              >Unlock Insights</Button
            >
            <div />
            <InlineLink
              href="https://app.campsite.bio/account/change-plan"
              style="margin-top: 1rem"
              >I already have an account</InlineLink
            >
          </div>
        </div>

        <div>
          <InsightsStatsBar
            :img-src="imgSrc"
            :size="isLargerScreen ? '2rem' : '1.25rem'"
            :views="59"
            :clicks="52"
            clicks-difference="15.3%"
            views-difference="5.1%"
          />
        </div>
      </div>
    </div>

    <SeenOn />

    <section class="analytics-pas grid grid--container">
      <Rte>
        <p>
          Your link in bio should be able to give you insights to convert more
          of your followers into customers.
        </p>
        <p>Performance dashboards lack insights, so if you want to:</p>
        <ul>
          <li>Make data-driven decisions</li>
          <li>Learn from your last marketing campaign</li>
          <li>See where your new customers came from</li>
          <li>Get more insights from your bio link data</li>
          <li>Compare and contrast your video performance</li>
        </ul>
        <p>
          Then the advanced analytics tooling available in our standalone add-on
          and Pro+ subscription are made for you.
        </p>
      </Rte>
    </section>

    <FeatureSection title="Introducing Advanced Analytics">
      <FeatureCard
        span="2"
        title="Top-line stats"
        bg-color="secondary"
        color="secondary"
      >
        <LineChartIcon slot="icon" />
        <VText
          >Get a high level view of your profile performance, including clicks,
          views, and CTR. Dig into each of your profiles with other metrics like
          events and reach.
        </VText>
        <img
          slot="img"
          src="~/assets/images/landing/analytics/overview-report.png"
          loading="lazy"
          alt="Overview report"
        />
      </FeatureCard>

      <FeatureCard
        span="2"
        title="Detailed reporting"
        bg-color="secondary"
        color="secondary"
      >
        <BarChartIcon slot="icon" />
        <VText
          >Our reports give you detailed breakdowns of your profile traffic. Get
          insights into your campaigns, events, a leaderboard to compare
          weekly/monthly performance, a channel report, and more.
        </VText>
        <img
          slot="img"
          src="~/assets/images/landing/analytics/video-report.png"
          loading="lazy"
          alt="Video report"
        />
      </FeatureCard>

      <FeatureCard title="Leaderboard report">
        <SvgIcon viewBox="0 0 24 24" slot="icon">
          <path
            d="M18 2C17.1 2 16 3 16 4H8C8 3 6.9 2 6 2H2V11C2 12 3 13 4 13H6.2C6.6 15 7.9 16.7 11 17V19.08C8 19.54 8 22 8 22H16C16 22 16 19.54 13 19.08V17C16.1 16.7 17.4 15 17.8 13H20C21 13 22 12 22 11V2H18M6 11H4V4H6V11M20 11H18V4H20V11Z"
          />
        </SvgIcon>
        <VText
          >Want to see what your best week or month was? Dive into your profile
          performance with our leaderboard report.
        </VText>
      </FeatureCard>
      <FeatureCard title="Channels report">
        <InstagramIcon slot="icon" />
        <VText
          >Review the performance of your Instagram account and get a breakdown
          of each post and how they performed.
        </VText>
      </FeatureCard>

      <FeatureCard
        span="2"
        title="Filtering"
        bg-color="secondary"
        color="secondary"
      >
        <FilterIcon slot="icon" />
        <VText gutter-bottom>
          Dive deeper into your analytics and filter by campaign, country,
          referrer, or device.
        </VText>
        <img
          slot="img"
          src="~/assets/images/landing/analytics/filter-range.png"
          loading="lazy"
          alt="Filtering your analytics"
        />
      </FeatureCard>

      <FeatureCard title="Download your data">
        <FileDownloadIcon slot="icon" />
        <VText
          >Looking to further analyze your profile analytics? Download your data
          as a .csv.
        </VText>
      </FeatureCard>
      <FeatureCard title="Lifetime data storage">
        <SvgIcon viewBox="0 0 24 24" slot="icon">
          <path
            d="M12,3C7.58,3 4,4.79 4,7C4,9.21 7.58,11 12,11C16.42,11 20,9.21 20,7C20,4.79 16.42,3 12,3M4,9V12C4,14.21 7.58,16 12,16C16.42,16 20,14.21 20,12V9C20,11.21 16.42,13 12,13C7.58,13 4,11.21 4,9M4,14V17C4,19.21 7.58,21 12,21C16.42,21 20,19.21 20,17V14C20,16.21 16.42,18 12,18C7.58,18 4,16.21 4,14Z"
          />
        </SvgIcon>
        <VText
          >We store all of your analytics data for life, so you always have
          access to it.
        </VText>
      </FeatureCard>
    </FeatureSection>

    <RaisedCtaSection
      title="Unlock insights with our advanced analytics"
      text="Get your first 10 days free when you sign up."
    >
      <Button
        href="https://app.campsite.bio/create-account"
        size="large"
        color="secondary"
        variant="hard-shadow"
        >Get Pro+</Button
      >
    </RaisedCtaSection>
  </article>
</template>

<script>
import {
  VText,
  Button,
  InlineLink,
  FilterIcon,
  SvgIcon,
  FileDownloadIcon,
  PrintIcon,
  InstagramIcon,
  LineChartIcon,
  BarChartIcon,
  InsightsStatsBar,
} from '@campsite-bio/component-lib';

import SeenOn from '@/components/seen-on-analytics';
import RaisedCtaSection from '@/components/sections/raised-cta-section';
import Rte from '@/components/rte';
import FeatureCard from '@/components/features/feature-card';
import FeatureSection from '@/components/features/feature-section';
import { useMediaQuery } from '@vueuse/core';
import { generateMeta } from '~/utils/generate-meta';

const imgSrc = require('~/assets/images/users/choice-hacking.png');

export default {
  components: {
    VText,
    Button,
    SeenOn,
    RaisedCtaSection,
    InsightsStatsBar,
    Rte,
    InlineLink,
    FilterIcon,
    LineChartIcon,
    BarChartIcon,
    SvgIcon,
    FileDownloadIcon,
    InstagramIcon,
    PrintIcon,
    FeatureCard,
    FeatureSection,
  },

  head() {
    return generateMeta({
      title: 'Link in bio Advanced Analytics & Insights - Campsite.bio',
      description:
        'Unlock insights with our advanced analytics. Available with our Pro+ plan or the analytics add-on.',
    });
  },

  layout: 'landing',

  setup() {
    const isLargerScreen = useMediaQuery('(min-width: 768px)');

    return {
      isLargerScreen,
      imgSrc,
    };
  },
};
</script>

<style lang="scss" scoped>
.analytics-top {
  background: var(--g-color-gray-100);
  background-image: linear-gradient(
      to bottom,
      rgba(255, 255, 255, 0),
      rgba(255, 255, 255, 1)
    ),
    url('~assets/images/mark-pattern-white.svg');
  background-size: auto, 80px auto;

  padding-bottom: 5rem;
  padding-top: 3rem;
  text-align: center;

  &__content {
    max-width: 800px;
    margin-left: auto;
    margin-right: auto;
    margin-bottom: 4rem;
  }
}

.analytics-top__title.analytics-top__title {
  margin-bottom: 1rem;
  font-size: 2.5rem;

  @media (min-width: 768px) {
    font-size: 3rem;
  }
}

.analytics-top__text.analytics-top__text {
  margin-bottom: 2rem;
  font-size: 1.25rem;
}

.analytics-pas {
  padding-bottom: 3rem;
  padding-top: 3rem;

  > div {
    font-size: 1.25rem;
    max-width: 700px;
    margin-left: auto;
    margin-right: auto;
  }
}

.analytics-content-title.analytics-content-title {
  text-align: center;
  margin: 3rem 0 4rem;
  font-size: 2rem;

  @media (min-width: 768px) {
    font-size: 2.5rem;
  }
}
</style>
